import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { queryHRAgent, getHRSessions, getHRSession, deleteHRSession } from '@/lib/api';
import { toast } from 'sonner';
import {
  Send,
  MessageSquareText,
  Plus,
  Loader2,
  Trash2,
  Bot,
  User,
  Clock,
  Scale,
  ChevronLeft,
  AlertCircle
} from 'lucide-react';

const HRAgentPage = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      setSessionsLoading(true);
      const res = await getHRSessions();
      setSessions(res.data);
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await getHRSession(sessionId);
      setCurrentSessionId(sessionId);
      setMessages(res.data.messages || []);
      setShowSidebar(false);
    } catch (err) {
      toast.error('Error al cargar la sesion');
    }
  };

  const startNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setQuestion('');
    setShowSidebar(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || loading) return;

    setQuestion('');
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setLoading(true);

    try {
      const res = await queryHRAgent(q, currentSessionId);
      const { answer, session_id } = res.data;
      setCurrentSessionId(session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: answer }]);
      loadSessions();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al consultar al agente IA';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${errorMsg}`, isError: true }]);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Eliminar esta conversacion?')) return;
    try {
      await deleteHRSession(sessionId);
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      toast.success('Conversacion eliminada');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-56px)] lg:h-screen flex" data-testid="hr-agent-page">
        {/* Sessions Sidebar */}
        <div className={cn(
          "border-r border-stone-200 bg-white flex flex-col transition-all duration-300",
          showSidebar ? "w-full lg:w-80" : "hidden lg:flex lg:w-80"
        )}>
          <div className="p-4 border-b border-stone-200">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold text-slate-900 text-base">Agente IA RRHH</h2>
            </div>
            <Button
              onClick={startNewChat}
              data-testid="new-chat-btn"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              Nueva Consulta
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessionsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Sin conversaciones</p>
            ) : (
              sessions.map(session => (
                <div
                  key={session.session_id}
                  onClick={() => loadSession(session.session_id)}
                  data-testid={`session-${session.session_id}`}
                  className={cn(
                    "group flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-colors",
                    currentSessionId === session.session_id
                      ? "bg-amber-50 border border-amber-200"
                      : "hover:bg-stone-50"
                  )}
                >
                  <MessageSquareText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 truncate">{session.title}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {formatTime(session.updated_at)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSession(e, session.session_id)}
                    data-testid={`delete-session-${session.session_id}`}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={cn(
          "flex-1 flex flex-col bg-stone-50",
          showSidebar ? "hidden lg:flex" : "flex"
        )}>
          {/* Chat Header */}
          <div className="p-4 border-b border-stone-200 bg-white flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(true)}
              className="lg:hidden p-1 rounded hover:bg-stone-100"
              data-testid="back-to-sessions-btn"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
              <Scale className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900 text-sm">Agente IA de RRHH</h3>
              <p className="text-xs text-slate-500">Convenio Colectivo + Estatuto Trabajadores</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4" data-testid="empty-chat-state">
                <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
                  <Scale className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Consulta Legal RRHH</h3>
                <p className="text-sm text-slate-500 max-w-md mb-6">
                  Pregunta sobre el Convenio Colectivo de Instalaciones Deportivas y Gimnasios
                  o el Estatuto de los Trabajadores.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                  {[
                    "Cual es la jornada maxima anual?",
                    "Cuantos dias de vacaciones corresponden?",
                    "Cual es el periodo de prueba?",
                    "Que dice sobre horas extras?"
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => { setQuestion(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                      className="text-left text-sm p-3 rounded-lg border border-stone-200 bg-white hover:bg-amber-50 hover:border-amber-200 text-slate-600 transition-colors"
                      data-testid={`suggestion-${q.slice(0, 15).replace(/\s/g, '-')}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                data-testid={`message-${idx}`}
                className={cn(
                  "flex gap-3 max-w-3xl",
                  msg.role === 'user' ? "ml-auto flex-row-reverse" : ""
                )}
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  msg.role === 'user' ? "bg-slate-200" : msg.isError ? "bg-red-100" : "bg-amber-100"
                )}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-slate-600" />
                  ) : msg.isError ? (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  ) : (
                    <Bot className="w-4 h-4 text-amber-700" />
                  )}
                </div>
                <div className={cn(
                  "rounded-xl px-4 py-3 text-sm leading-relaxed max-w-[85%]",
                  msg.role === 'user'
                    ? "bg-slate-800 text-white"
                    : msg.isError
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-white text-slate-700 border border-stone-200 shadow-sm"
                )}>
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3" data-testid="loading-indicator">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-amber-700" />
                </div>
                <div className="bg-white rounded-xl px-4 py-3 border border-stone-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Consultando documentos legales...
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 pb-6 border-t border-stone-200 bg-white">
            <form onSubmit={handleSubmit} className="flex gap-2 max-w-3xl mx-auto pr-12 lg:pr-0">
              <Input
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Escribe tu consulta sobre RRHH..."
                disabled={loading}
                data-testid="hr-agent-input"
                className="flex-1"
                autoFocus
              />
              <Button
                type="submit"
                disabled={loading || !question.trim()}
                data-testid="hr-agent-send-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white px-4"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
            <p className="text-xs text-slate-400 text-center mt-2 pr-12 lg:pr-0">
              Respuestas basadas en el Convenio Colectivo (BOE-A-2024-1506) y el Estatuto de los Trabajadores (RDL 2/2015)
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HRAgentPage;
